import * as React from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import classNames from 'classnames'

import { Flex } from '@grammarly/design-system'

import styles from './ChatInput.module.scss'

export type ChatInputProps = {
  /**
   * Callback function called when user submits a message
   * @param content The message text entered by the user
   * @param options Additional options (like context)
   */
  readonly onSubmit: (content: string, options?: { readonly includeContext: boolean }) => void
  /**
   * Reference to the textarea element
   * This is used to focus the input after submitting a message or when the component mounts
   */
  readonly textAreaRef: React.RefObject<HTMLTextAreaElement>
  /**
   * The current value of the input
   */
  readonly value: string
  /**
   * The callback function to handle input changes
   */
  readonly onChange: (value: string) => void
  /**
   * Whether the input should be focused automatically when the component mounts
   */
  readonly autoFocus?: boolean
  /**
   * Optional function to render custom controls below the input
   * This can be used to add buttons or other interactive elements
   * @param props Contains a handleSubmit function to submit the message
   * @returns React node to render as controls
   */
  readonly controls?: (props: { handleSubmit: () => void }) => React.ReactNode
  /**
   * Optional React node to render as a context chip. If context is provided.
   */
  readonly contextChip?: React.ReactNode
  /**
   * When disabled, the input will not allow user to input a message
   */
  readonly disabled?: boolean
  /**
   * Optional placeholder text for the input
   */
  readonly placeholder?: string
  /**
   * Optional icon to render inside the input
   */
  readonly icon?: React.ReactNode
  /**
   * Optional className to add to the input
   */
  readonly className?: string
  /**
   * Whether to always show the bottom controls, even if not focused with no value
   */
  readonly alwaysShowControls?: boolean
}

/**
 * Input to let user type a message to the assistant
 * @param props Props
 * @returns Chat input component
 */
export function ChatInput(props: ChatInputProps) {
  const { onSubmit, disabled, textAreaRef, value, onChange, autoFocus, alwaysShowControls } = props
  const inputControlsRef = React.useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = React.useState(false)
  const hasContext = !!props.contextChip
  const hasValue = value.trim() !== ''

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }

  const handleSubmit = () => {
    const trimmedValue = value.trim()
    if (trimmedValue === '') {
      return
    }

    onChange('')
    onSubmit(trimmedValue, { includeContext: hasContext })
    textAreaRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled) {
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleInputControlsClick = () => {
    textAreaRef.current?.focus()
  }

  React.useEffect(() => {
    if (value.trim() === '') {
      setIsActive(false)
    } else {
      setIsActive(true)
    }
  }, [value])

  const showBottomControls = alwaysShowControls || isActive || autoFocus || hasValue

  return (
    <div
      className={classNames(styles.chatInputControls, props.className, {
        [styles.hasNoContext]: !hasContext,
        [styles.disabled]: disabled,
      })}
      ref={inputControlsRef}
      onClick={handleInputControlsClick}
    >
      {props.contextChip}
      <Flex direction="row" align="start" justify="start" gap={2}>
        {props.icon}
        <TextareaAutosize
          disabled={disabled}
          ref={textAreaRef}
          autoFocus={autoFocus}
          minRows={1}
          maxRows={10}
          value={value}
          placeholder={props.placeholder || 'Ask AI'}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={styles.chatInputTextarea}
          onClick={() => setIsActive(true)}
          onBlur={() => setIsActive(false)}
        />
      </Flex>
      {showBottomControls && props.controls && props.controls({ handleSubmit })}
    </div>
  )
}
